/**
 * Permanent right-side leaderboard for Card Match round/reveal scenes, with
 * an auto-cycling view toggle. Mirrors the anagram / who-am-i / horse-race
 * side panels for cross-game visual parity.
 *
 * Two views, ~12s apart, cross-fade:
 *   - "ALL TIME · TOP 50" — top 50 across all sessions for the tenant.
 *   - "THIS SESSION"      — current game session standings.
 *
 * Layout: 380px wide, absolutely positioned at left=1500..1880, top=120,
 * height=840 — same coordinates as the anagram panel so all five games line
 * up identically when an OBS scene cuts between them.
 */

import type { LbRow } from '@sg/shared';
import type { SideLbRow } from '../state/types.js';

export interface SideLeaderboardHandle {
  el: HTMLDivElement;
  setVisible(v: boolean): void;
  setSessionRows(rows: SideLbRow[]): void;
  setAllTimeRows(rows: LbRow[] | SideLbRow[]): void;
  dispose(): void;
}

/** Auto-cycle interval (ms) between the two views. */
const VIEW_INTERVAL_MS = 12_000;
/** Cross-fade duration (ms). */
const FADE_MS = 360;

const PANEL_STYLES = `
  @keyframes cm-sidelb-in {
    from { opacity: 0; transform: translateX(12px); }
    to   { opacity: 1; transform: translateX(0);    }
  }
  .cm-sidelb {
    position: absolute;
    top: 120px;
    left: 1500px;
    width: 380px;
    height: 840px;
    z-index: 50;
    pointer-events: none;
    animation: cm-sidelb-in 280ms ease-out;
    transition: opacity ${FADE_MS}ms ease;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(31, 27, 75, 0.55) 0%, rgba(13, 13, 20, 0.85) 100%);
    border: 2px solid var(--sg-primary, #8A2BE2);
    border-radius: 14px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.45), 0 0 32px rgba(138, 43, 226, 0.18);
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    overflow: hidden;
    box-sizing: border-box;
  }
  .cm-sidelb.fading-out { opacity: 0; }
  .cm-sidelb.hidden { display: none; }
  .cm-sidelb-title {
    font-family: 'Oswald', 'Bebas Neue', Impact, sans-serif;
    font-size: 1.25rem;
    letter-spacing: 0.22em;
    text-align: center;
    /* White title for visual parity with the round HUD header
       (cyan was readable but the user wanted white across HUD text).
       Brand cyan preserved as the soft surrounding glow + divider. */
    color: #FFFFFF;
    text-shadow: 0 0 14px rgba(0, 229, 255, 0.4), 0 2px 4px rgba(0, 0, 0, 0.55);
    padding: 10px 8px 8px 8px;
    border-bottom: 1px solid rgba(0, 229, 255, 0.30);
    flex: 0 0 auto;
    text-transform: uppercase;
  }
  .cm-sidelb-head {
    display: grid;
    grid-template-columns: 40px 1fr 86px;
    gap: 8px;
    padding: 6px 12px 4px 12px;
    font-family: 'Oswald', 'Bebas Neue', Impact, sans-serif;
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    color: #94A3B8;
    border-bottom: 1px dashed rgba(255, 255, 255, 0.12);
    flex: 0 0 auto;
    text-transform: uppercase;
  }
  .cm-sidelb-head > div:last-child { text-align: right; }
  .cm-sidelb-body { flex: 1 1 auto; min-height: 0; overflow: hidden; }
  .cm-sidelb-list { display: flex; flex-direction: column; padding: 4px 0; }
  .cm-sidelb-row {
    display: grid;
    grid-template-columns: 40px 1fr 86px;
    gap: 8px;
    align-items: center;
    padding: 8px 12px;
    font-size: 1.0rem;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }
  .cm-sidelb-row:first-child { border-top: none; }
  .cm-sidelb-rank {
    font-family: 'Oswald', 'Bebas Neue', Impact, sans-serif;
    font-weight: 700;
    text-align: center;
    font-size: 1.1rem;
    color: #94A3B8;
  }
  .cm-sidelb-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 600;
    color: #E2E8F0;
  }
  .cm-sidelb-points {
    text-align: right;
    font-weight: 700;
    color: var(--sg-primary, #8A2BE2);
    font-variant-numeric: tabular-nums;
    font-size: 1.05rem;
    text-shadow: 0 0 10px rgba(138, 43, 226, 0.45);
  }
  .cm-sidelb-row.cm-r1 { background: linear-gradient(90deg, rgba(255, 196, 61, 0.18), rgba(255, 196, 61, 0.0)); }
  .cm-sidelb-row.cm-r2 { background: linear-gradient(90deg, rgba(192, 192, 192, 0.15), rgba(192, 192, 192, 0.0)); }
  .cm-sidelb-row.cm-r3 { background: linear-gradient(90deg, rgba(205, 127, 50, 0.15), rgba(205, 127, 50, 0.0)); }
  .cm-sidelb-row.cm-r1 .cm-sidelb-rank { color: #FFD166; text-shadow: 0 0 10px rgba(255, 196, 61, 0.7); }
  .cm-sidelb-row.cm-r2 .cm-sidelb-rank { color: #D9D9D9; text-shadow: 0 0 8px rgba(217, 217, 217, 0.5); }
  .cm-sidelb-row.cm-r3 .cm-sidelb-rank { color: #E0A060; text-shadow: 0 0 8px rgba(205, 127, 50, 0.5); }
  .cm-sidelb-empty {
    padding: 20px 14px;
    text-align: center;
    color: #94A3B8;
    font-style: italic;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

function ensurePanelStyles(): void {
  if (document.getElementById('cm-sidelb-style')) return;
  const s = document.createElement('style');
  s.id = 'cm-sidelb-style';
  s.textContent = PANEL_STYLES;
  document.head.appendChild(s);
}

function medalFor(rank: number): string {
  if (rank === 1) return 'cm-r1';
  if (rank === 2) return 'cm-r2';
  if (rank === 3) return 'cm-r3';
  return '';
}

type View = 'all_time' | 'session';

interface NormalizedRow {
  rank: number;
  display_name: string;
  total_points: number;
}

function normalize(rows: ReadonlyArray<LbRow | SideLbRow>): NormalizedRow[] {
  return rows.map((r) => ({
    rank: r.rank,
    display_name: r.display_name,
    total_points: (r as { total_points: number }).total_points,
  }));
}

export function createSideLeaderboard(stage: HTMLElement): SideLeaderboardHandle {
  ensurePanelStyles();

  const el = document.createElement('div');
  el.className = 'cm-sidelb';
  el.setAttribute('data-role', 'game-side-lb');
  el.setAttribute('data-testid', 'cm-side-lb');
  el.innerHTML = `
    <div class="cm-sidelb-title" data-role="title" data-testid="cm-sidelb-title">ALL TIME &middot; TOP 50</div>
    <div class="cm-sidelb-head"><div>#</div><div>PLAYER</div><div>POINTS</div></div>
    <div class="cm-sidelb-body">
      <div class="cm-sidelb-list" data-role="rows">
        <div class="cm-sidelb-empty">No scores yet &mdash; be the first to match!</div>
      </div>
    </div>
  `;
  stage.appendChild(el);

  const titleEl = el.querySelector<HTMLDivElement>('[data-role="title"]')!;
  const rowsContainer = el.querySelector<HTMLDivElement>('[data-role="rows"]')!;

  let allTimeRows: NormalizedRow[] = [];
  let sessionRows: NormalizedRow[] = [];
  let view: View = 'all_time';
  let cycleTimer: ReturnType<typeof setInterval> | null = null;

  function renderRows(rows: NormalizedRow[]): void {
    if (rows.length === 0) {
      rowsContainer.innerHTML =
        '<div class="cm-sidelb-empty">No scores yet &mdash; be the first to match!</div>';
      return;
    }
    const fragment = document.createDocumentFragment();
    for (const r of rows.slice(0, 10)) {
      const row = document.createElement('div');
      row.className = `cm-sidelb-row ${medalFor(r.rank)}`.trim();
      row.innerHTML = `
        <div class="cm-sidelb-rank">${r.rank}</div>
        <div class="cm-sidelb-name"></div>
        <div class="cm-sidelb-points">${r.total_points.toLocaleString()}</div>
      `;
      const nameEl = row.querySelector<HTMLDivElement>('.cm-sidelb-name')!;
      nameEl.textContent = r.display_name;
      fragment.appendChild(row);
    }
    rowsContainer.replaceChildren(fragment);
  }

  function applyView(): void {
    const rows = view === 'all_time' ? allTimeRows : sessionRows;
    titleEl.innerHTML = view === 'all_time' ? 'ALL TIME &middot; TOP 50' : 'THIS SESSION';
    titleEl.setAttribute('data-view', view);
    renderRows(rows);
  }

  function fadeAndSwap(): void {
    el.classList.add('fading-out');
    setTimeout(() => {
      view = view === 'all_time' ? 'session' : 'all_time';
      applyView();
      el.classList.remove('fading-out');
    }, FADE_MS);
  }

  function startCycle(): void {
    if (cycleTimer !== null) return;
    cycleTimer = setInterval(fadeAndSwap, VIEW_INTERVAL_MS);
  }
  function stopCycle(): void {
    if (cycleTimer !== null) {
      clearInterval(cycleTimer);
      cycleTimer = null;
    }
  }

  function setAllTimeRows(rows: LbRow[] | SideLbRow[]): void {
    allTimeRows = normalize(rows);
    if (view === 'all_time') applyView();
  }

  function setSessionRows(rows: SideLbRow[]): void {
    sessionRows = normalize(rows);
    if (view === 'session') applyView();
  }

  function setVisible(v: boolean): void {
    el.classList.toggle('hidden', !v);
    if (v) startCycle();
    else stopCycle();
  }

  // Start with the all-time view rendered (empty state until data lands).
  applyView();
  startCycle();

  return {
    el,
    setVisible,
    setSessionRows,
    setAllTimeRows,
    dispose() {
      stopCycle();
      el.remove();
    },
  };
}
